/**
 * Общие детали экранов Гендиректора, которые не должны вылезать из своего блока
 * ни на какой ширине: шапка карточки банковского счёта, строка «подпись —
 * значение», ряд кнопок в шапке раздела, кнопка и значок с длинной подписью.
 */
import styled from 'styled-components';
import type { CeoBankAccount } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { bankAccountSource } from './ceoFormat';

/** Кнопка ($size="sm") с длинной подписью: на узком экране подпись переносится
 *  на вторую строку, а не выходит за край блока. */
export const WrapButton = styled(Button)`
  max-width: 100%;
  height: auto;
  min-height: 32px;
  white-space: normal;
  text-align: center;
`;

/** Значок не шире своего блока: длинная подпись переносится внутри значка. */
export const WrapBadge = styled(Badge)`
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
`;

/** Кнопки и ссылки в шапке раздела: переносятся рядами и сжимаются вместе с экраном. */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  min-width: 0;
`;

/** Подпись слева, значение справа. В тесной карточке сначала переносится
 *  подпись (значение остаётся справа на первой строке), а когда и так не
 *  помещается — значение уходит строкой ниже к правому краю. Вплотную к подписи
 *  и за край карточки оно не встаёт. */
export const SplitRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 2px 10px;
  font-size: 12px;

  & > :first-child {
    flex: 1 1 90px;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  & > :last-child {
    flex: none;
    max-width: 100%;
    margin-left: auto;
    text-align: right;
    overflow-wrap: anywhere;
  }
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const BankName = styled.div`
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const DemoBadge = styled(WrapBadge)`
  font-size: 10px;
`;

/** Шапка карточки счёта: название банка — своей строкой, под ним значки
 *  «Демо-данные» и API банка. Раньше всё стояло в один ряд без переноса, и в
 *  карточке шириной 260 px значок API уезжал за её правый край. */
export function BankAccountHead({ account }: { account: CeoBankAccount }) {
  const source = bankAccountSource(account);
  return (
    <Head>
      <BankName>{account.bank_name}</BankName>
      <BadgeRow>
        {account.id.startsWith('ce000001-') && <DemoBadge $tone="neutral">Демо-данные</DemoBadge>}
        <WrapBadge $tone={source.tone}>{source.label}</WrapBadge>
      </BadgeRow>
    </Head>
  );
}
